// src/contexts/NotificationContext.tsx
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  IconButton,
  Box,
  Typography,
  LinearProgress,
} from '@mui/material';
import type { SlideProps, AlertColor } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Types
export interface NotificationOptions {
  title?: string;
  message: string;
  type?: AlertColor;
  duration?: number;
  action?: React.ReactNode;
  persistent?: boolean;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

interface Notification extends NotificationOptions {
  id: string;
  createdAt: number;
}

interface NotificationContextType {
  // Core methods
  notify: (options: NotificationOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;

  // Convenience methods
  success: (message: string, title?: string) => string;
  error: (message: string, title?: string) => string;
  warning: (message: string, title?: string) => string;
  info: (message: string, title?: string) => string;

  // Promise-based notification
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: any) => string);
    }
  ) => Promise<T>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

// Slide transition
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

// Get icon based on type
const getIcon = (type: AlertColor) => {
  const iconProps = { sx: { fontSize: 24 } };
  switch (type) {
    case 'success':
      return <CheckCircleOutlineIcon {...iconProps} />;
    case 'error':
      return <ErrorOutlineIcon {...iconProps} />;
    case 'warning':
      return <WarningAmberIcon {...iconProps} />;
    case 'info':
    default:
      return <InfoOutlinedIcon {...iconProps} />;
  }
};

// Generate unique ID
const generateId = () => `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Default durations by type
const DEFAULT_DURATIONS: Record<AlertColor, number> = {
  success: 4000,
  error: 6000,
  warning: 5000,
  info: 4000,
};

// Provider component
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState<Map<string, string>>(new Map());

  // Dismiss a notification
  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setLoadingNotifications((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Dismiss all notifications
  const dismissAll = useCallback(() => {
    setNotifications([]);
    setLoadingNotifications(new Map());
  }, []);

  // Core notify function
  const notify = useCallback((options: NotificationOptions): string => {
    const id = generateId();
    const type = options.type || 'info';

    const notification: Notification = {
      ...options,
      id,
      type,
      duration: options.persistent ? null : (options.duration || DEFAULT_DURATIONS[type]),
      createdAt: Date.now(),
      position: options.position || { vertical: 'bottom', horizontal: 'right' },
    } as Notification;

    setNotifications((prev) => {
      // Limit to 5 notifications max
      const limited = prev.length >= 5 ? prev.slice(1) : prev;
      return [...limited, notification];
    });

    return id;
  }, []);

  // Convenience methods
  const success = useCallback((message: string, title?: string) => {
    return notify({ message, title, type: 'success' });
  }, [notify]);

  const error = useCallback((message: string, title?: string) => {
    return notify({ message, title, type: 'error' });
  }, [notify]);

  const warning = useCallback((message: string, title?: string) => {
    return notify({ message, title, type: 'warning' });
  }, [notify]);

  const info = useCallback((message: string, title?: string) => {
    return notify({ message, title, type: 'info' });
  }, [notify]);

  // Promise-based notification (like toast.promise)
  const promiseNotify = useCallback(async <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: any) => string);
    }
  ): Promise<T> => {
    const id = generateId();

    // Show loading state
    setLoadingNotifications((prev) => new Map(prev).set(id, messages.loading));

    try {
      const result = await promise;
      setLoadingNotifications((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });

      const successMessage = typeof messages.success === 'function'
        ? messages.success(result)
        : messages.success;
      success(successMessage);

      return result;
    } catch (err) {
      setLoadingNotifications((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });

      const errorMessage = typeof messages.error === 'function'
        ? messages.error(err)
        : messages.error;
      error(errorMessage);

      throw err;
    }
  }, [success, error]);

  // Context value
  const contextValue = useMemo<NotificationContextType>(() => ({
    notify,
    dismiss,
    dismissAll,
    success,
    error,
    warning,
    info,
    promise: promiseNotify,
  }), [notify, dismiss, dismissAll, success, error, warning, info, promiseNotify]);

  // Handle close
  const handleClose = (id: string) => (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    dismiss(id);
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Loading notifications */}
      {Array.from(loadingNotifications.entries()).map(([id, message], index) => (
        <Snackbar
          key={id}
          open={true}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            bottom: { xs: 24 + (index * 80), sm: 24 + (index * 80) },
          }}
        >
          <Alert
            severity="info"
            icon={false}
            sx={{
              minWidth: 300,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderTopColor: 'transparent',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
              <Typography variant="body2" fontWeight={500}>
                {message}
              </Typography>
            </Box>
            <LinearProgress
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 3,
              }}
            />
          </Alert>
        </Snackbar>
      ))}

      {/* Regular notifications */}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.duration}
          onClose={handleClose(notification.id)}
          anchorOrigin={notification.position || { vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={SlideTransition}
          sx={{
            bottom: {
              xs: 24 + (index * 80) + (loadingNotifications.size * 80),
              sm: 24 + (index * 80) + (loadingNotifications.size * 80)
            },
          }}
        >
          <Alert
            severity={notification.type}
            icon={getIcon(notification.type || 'info')}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {notification.action}
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => dismiss(notification.id)}
                  sx={{
                    opacity: 0.7,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
            sx={{
              minWidth: 320,
              maxWidth: 450,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              borderRadius: 2,
              alignItems: 'flex-start',
              '& .MuiAlert-icon': {
                marginTop: notification.title ? 0.5 : 0,
              },
              '& .MuiAlert-message': {
                padding: notification.title ? '4px 0' : '2px 0',
              },
              // Custom colors for each type
              ...(notification.type === 'success' && {
                bgcolor: '#f0fdf4',
                color: '#166534',
                border: '1px solid #bbf7d0',
                '& .MuiAlert-icon': { color: '#16a34a' },
              }),
              ...(notification.type === 'error' && {
                bgcolor: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                '& .MuiAlert-icon': { color: '#dc2626' },
              }),
              ...(notification.type === 'warning' && {
                bgcolor: '#fffbeb',
                color: '#92400e',
                border: '1px solid #fde68a',
                '& .MuiAlert-icon': { color: '#d97706' },
              }),
              ...(notification.type === 'info' && {
                bgcolor: '#eff6ff',
                color: '#1e40af',
                border: '1px solid #bfdbfe',
                '& .MuiAlert-icon': { color: '#2563eb' },
              }),
            }}
          >
            {notification.title && (
              <AlertTitle sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.95rem' }}>
                {notification.title}
              </AlertTitle>
            )}
            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
              {notification.message}
            </Typography>
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

// Custom hook
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
